import { PayloadAction } from '@reduxjs/toolkit';
import { all, call, select, takeEvery } from 'typed-redux-saga';

import { selectIsMeetingRoomIntegration, selectIsTeleMessenger } from '../../../shared/features/config/selectors';
import { parentFrameCommunicator } from '../../../shared/features/parent-frame-communicator';
import { GRID_VIEW } from '../constants';
import { getIsChatLoading } from '../features/chat/slice';
import { selectIsCloudRecordingAvailable } from '../features/cloud-recording';
import {
    connectedToConferenceAction,
    disconnectedFromConferenceDoneAction,
    userJoinAction,
    userLeftAction,
} from '../features/conference/slice';
import { getPermissions } from '../features/permissions';
import { selectIsSummarizationAvailable } from '../features/summarization';
import {
    getActiveGridView,
    getChatWidgetVisible,
    getConferenceId,
    getSelfMutedState,
    getSidebarContent,
    selectIsCalendarSummarizationReceiveAvailable,
    selectIsPermissionsExtendedEnabled,
    selectMeetingName,
    selectSelfReactionType,
} from '../selectors';
import { DisconnectPayload, SidebarContent, UserParams } from '../types';
import { OutgoingEvents } from './types';

function* handleSelfConnectTeleMessengerSaga() {
    const extendedRolePermissionsEnabled = yield* select(selectIsPermissionsExtendedEnabled);
    const cloudRecordingAvailable = yield* select(selectIsCloudRecordingAvailable);
    const summarizationAvailable = yield* select(selectIsSummarizationAvailable);
    const calendarSummarizationReceiveAvailable = yield* select(selectIsCalendarSummarizationReceiveAvailable);
    const permissions = yield* select(getPermissions);

    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.CONNECTED,
        payload: {
            id: 'self',
            clientConfiguration: {
                extended_role_permissions_enabled: extendedRolePermissionsEnabled,
                cloud_recording_available: cloudRecordingAvailable,
                summarization_available: summarizationAvailable,
                calendar_summarization_receive_available: calendarSummarizationReceiveAvailable,
            },
            permissionsState: permissions,
        },
    });
}

function* handleSelfConnect() {
    const isMeetingRoomIntegration = yield* select(selectIsMeetingRoomIntegration);
    const isTeleMessenger = yield* select(selectIsTeleMessenger);

    if (isMeetingRoomIntegration) {
        const meetingName = yield* select(selectMeetingName);
        const meetingId = yield* select(getConferenceId);

        yield* call(parentFrameCommunicator.sendMessage, '*', {
            event: OutgoingEvents.MEETING_INFO,
            payload: { meetingName, meetingId },
        });

        const mutedState = yield* select(getSelfMutedState);
        const gridMode = yield* select(getActiveGridView);
        const chatVisible = yield* select(getChatWidgetVisible);
        const sidebarContent = yield* select(getSidebarContent);
        const selfReactionType = yield* select(selectSelfReactionType);
        const participantsListVisible = sidebarContent === SidebarContent.Participants;
        const isChatLoading = yield* select(getIsChatLoading);

        yield* call(parentFrameCommunicator.sendMessage, '*', {
            event: OutgoingEvents.UI_STATE_CHANGED,
            payload: {
                videoEnabled: !mutedState?.videoMuted,
                audioEnabled: !mutedState?.audioMuted,
                reaction: selfReactionType || null,
                gridView: gridMode || GRID_VIEW.DEFAULT,
                chatVisible: chatVisible,
                participantsListVisible: participantsListVisible,
                isChatLoading,
            },
        });
    }

    if (isTeleMessenger) {
        yield* call(handleSelfConnectTeleMessengerSaga);
        return;
    }
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.CONNECTED,
        payload: { id: 'self' },
    });
}

function* handleSelfDisconnect({ payload: { error, source } }: PayloadAction<DisconnectPayload>) {
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.DISCONNECTED,
        payload: { id: 'self', error, source },
    });
}

function* handleUserJoin({ payload: { id } }: PayloadAction<UserParams>) {
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.CONNECTED,
        payload: { id },
    });
}

function* handleUserLeft({ payload: { id } }: PayloadAction<UserParams>) {
    yield* call(parentFrameCommunicator.sendMessage, '*', {
        event: OutgoingEvents.DISCONNECTED,
        payload: { id },
    });
}

export function* handleConnection(): Generator {
    yield* all([
        takeEvery(connectedToConferenceAction.type, handleSelfConnect),
        takeEvery(disconnectedFromConferenceDoneAction.type, handleSelfDisconnect),
        takeEvery(userJoinAction.type, handleUserJoin),
        takeEvery(userLeftAction.type, handleUserLeft),
    ]);
}
